/**
 * Pure projection maths for the Schedule Timeline (#946).
 *
 * Kept free of React and of the DOM so the rules that make the surface honest
 * — unknown durations excluded from occupancy, contention requiring a real
 * scheduled run — are testable directly.
 */

const MIN = 60_000;

/** A connector starting at least this often is background hum, not an event an
 *  operator schedules around: it forms the ribbon's baseline instead of lanes. */
export const CONTINUOUS_MAX_GAP_MIN = 60;

/** Two runs brushing past each other for a minute is not a contention window. */
export const MIN_CONTENTION_MIN = 2;

/** Nominal width so an unknown-duration run is visible. Never presented as a
 *  duration — the bar dissolves rather than ending. */
export const UNKNOWN_NOMINAL_SEC = 600;

/**
 * Snap an instant down to its wall-clock minute (#1030).
 *
 * The window bounds arrive as the API's `now()`, so they carry seconds. Bucketing
 * from an unfloored origin puts every bucket boundary on that second-offset, and
 * inverting a bucket index back to an instant (`windowStart + index * MIN`) then
 * lands up to 59s before the run it names — which `formatClock` truncates to the
 * previous minute. Floor the origin once and the inverse is exact.
 */
export function floorToMinute(ms: number): number {
  return Math.floor(ms / MIN) * MIN;
}

export interface ProjectedRun {
  /** epoch ms */
  start: number;
  /** epoch ms — start + median duration, or the nominal width when unknown */
  end: number;
  unknown: boolean;
}

export interface ProjectedLane {
  configId: number;
  name: string;
  connectorType: string;
  cron: string;
  timezone: string;
  medianSeconds: number | null;
  p90Seconds: number | null;
  sampleCount: number;
  runs: ProjectedRun[];
  continuous: boolean;
}

/**
 * Per-minute occupancy across the window.
 *
 * Unknown-duration runs are excluded: their length is not measurable, and
 * counting them at a guessed width would invent contention that may not exist.
 */
export function occupancyDepth(
  lanes: ProjectedLane[],
  windowStart: number,
  windowEnd: number,
): number[] {
  const minutes = Math.round((windowEnd - windowStart) / MIN);
  const depth = new Array<number>(Math.max(minutes, 0)).fill(0);

  for (const lane of lanes) {
    for (const run of lane.runs) {
      if (run.unknown) continue;
      const from = Math.max(0, Math.floor((run.start - windowStart) / MIN));
      // A sub-minute run still occupies its minute, so clamp the end above `from`.
      const to = Math.min(
        depth.length,
        Math.max(from + 1, Math.ceil((run.end - windowStart) / MIN)),
      );
      for (let i = from; i < to; i++) depth[i] += 1;
    }
  }
  return depth;
}

/**
 * Contiguous `[start, end)` minute ranges worth surfacing: two or more runs at
 * once where at least one is a non-continuous (scheduled batch) run, lasting at
 * least MIN_CONTENTION_MIN.
 */
export function contentionRanges(
  totalDepth: number[],
  eventDepth: number[],
): Array<[number, number]> {
  const hit = (i: number) => totalDepth[i] >= 2 && eventDepth[i] >= 1;
  const ranges: Array<[number, number]> = [];
  let open: number | null = null;

  for (let i = 0; i < totalDepth.length; i++) {
    if (hit(i) && open === null) open = i;
    else if (!hit(i) && open !== null) {
      ranges.push([open, i]);
      open = null;
    }
  }
  if (open !== null) ranges.push([open, totalDepth.length]);

  return ranges.filter(([a, b]) => b - a >= MIN_CONTENTION_MIN);
}

/** The longest stretch of zero occupancy — the slot an operator is hunting for. */
export function longestClearWindow(depth: number[]): {
  lengthMinutes: number;
  startIndex: number;
} {
  let best = { lengthMinutes: 0, startIndex: 0 };
  let open: number | null = null;

  for (let i = 0; i < depth.length; i++) {
    if (depth[i] === 0 && open === null) open = i;
    else if (depth[i] !== 0 && open !== null) {
      if (i - open > best.lengthMinutes) best = { lengthMinutes: i - open, startIndex: open };
      open = null;
    }
  }
  if (open !== null && depth.length - open > best.lengthMinutes) {
    best = { lengthMinutes: depth.length - open, startIndex: open };
  }
  return best;
}

const STEPS = [
  5 * MIN,
  10 * MIN,
  15 * MIN,
  30 * MIN,
  60 * MIN,
  2 * 60 * MIN,
  3 * 60 * MIN,
  6 * 60 * MIN,
  12 * 60 * MIN,
  24 * 60 * MIN,
];

/** Smallest step that keeps the axis to at most 13 ticks. */
export function niceStep(spanMs: number): number {
  for (const step of STEPS) if (spanMs / step <= 13) return step;
  return 24 * 60 * MIN;
}
